import type { ApolloClient } from '@apollo/client';
import type {
  AirgapAPI,
  ConsentChangeEventPayload,
  TrackingConsent,
  TrackingConsentDetails,
} from '@transcend-io/airgap.js-types';

import {
  ConsentPreferenceValue,
  type ConsentPurposePreferenceInput,
  getEffectiveConsentPreference,
  recordConsentPreferenceEvent,
} from '@/api';
import {
  CONSENT_CHANGE_EVENT,
  CONSENT_RECORDED_PREFERENCE_KEY,
  CONSENT_RESTORE_ATTEMPTS,
  CONSENT_RESTORE_SKIP_REPORTED_KEY,
  CONSENT_SCOPE_LENGTH,
  CONSENT_UNAUTHORIZED_CODE,
} from '@/constants';
import { preferenceSignature, toAirgapPurposes, toPreferenceInput } from '@/helpers';
import airbrake from '@/layouts/ErrorBoundary/airbrake-client';

import {
  createInteractionEventSource,
  type InteractionEventSource,
  whenAirgapReady,
} from './airgap-client';

type StoredPreference = {
  consentGivenAt: string;
  preferences: ConsentPurposePreferenceInput[];
};

type SyncContext = {
  client: ApolloClient<object>;
  airgap: AirgapAPI;
  scope: string;
  restoring: boolean;
  // Lets the user's own answer release a restore still waiting for an interaction.
  interactions: InteractionEventSource | null;
  // Chained so submissions reach the history in order and see each other's markers.
  submission: Promise<unknown>;
};

const notify = (error: unknown) => {
  void airbrake.notify({ error });
};

const recordedPreferenceKey = (scope: string) => `${CONSENT_RECORDED_PREFERENCE_KEY}.${scope}`;
const restoreSkipReportedKey = (scope: string) => `${CONSENT_RESTORE_SKIP_REPORTED_KEY}.${scope}`;

// sessionStorage throws in Safari private mode; a lost marker only costs a repeat call.
const readStorage = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // The backend suppresses the duplicate instead.
  }
};

const removeStorage = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing stored, nothing to remove.
  }
};

// Each new session token is a new scope, so markers from a rotated token are dropped.
const pruneForeignScopes = (scope: string): void => {
  try {
    Object.keys(sessionStorage)
      .filter(
        (key) =>
          key.startsWith(CONSENT_RECORDED_PREFERENCE_KEY) ||
          key.startsWith(CONSENT_RESTORE_SKIP_REPORTED_KEY)
      )
      .filter((key) => !key.endsWith(`.${scope}`))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage is unavailable, so there is nothing to prune.
  }
};

const fetchStoredPreference = async (context: SyncContext): Promise<StoredPreference | null> => {
  const { data } = await context.client.query({
    query: getEffectiveConsentPreference,
    fetchPolicy: 'no-cache',
    // The client default "all" would turn a failed read into "no preference" and skip the restore.
    errorPolicy: 'none',
  });
  const payload = data?.effectiveConsentPreference;
  // The session ended while the page stayed open; the next sign-in reads it again.
  if (payload?.error?.code === CONSENT_UNAUTHORIZED_CODE) return null;
  if (payload?.error) throw new Error(`Consent preference read failed: ${payload.error.message}`);
  if (!payload?.preference) return null;

  return {
    consentGivenAt: String(payload.preference.consentGivenAt),
    preferences: payload.preference.preferences,
  };
};

const writeRecordedSignature = (context: SyncContext, signature: string): void => {
  writeStorage(recordedPreferenceKey(context.scope), signature);
};

const submitConsent = async (
  context: SyncContext,
  consent: TrackingConsentDetails,
  signals?: Iterable<string> | null
): Promise<void> => {
  const preferences = toPreferenceInput(consent.purposes);
  // The backend rejects a snapshot with nothing configurable.
  if (!preferences.length) return;

  const signature = preferenceSignature(preferences);
  // The backend dedupes too; this spares it a row lock per page load.
  if (readStorage(recordedPreferenceKey(context.scope)) === signature) return;

  const { data } = await context.client.mutate({
    mutation: recordConsentPreferenceEvent,
    variables: {
      input: {
        consentGivenAt: consent.timestamp,
        preferences,
        legalRegimes: [...context.airgap.getRegimes()],
        // The backend derives consent_method "implicit" from a GPC or DNT signal.
        privacySignals: signals ? [...signals] : [],
        airgapVersion: context.airgap.version,
      },
    },
  });
  // Origin and validation failures come back as a payload error, not a GraphQL error.
  const error = data?.recordConsentPreferenceEvent?.error;
  // The session ended while the page stayed open. Left unmarked, so the next sign-in sends it again.
  if (error?.code === CONSENT_UNAUTHORIZED_CODE) return;
  if (error) throw new Error(`Consent preference not recorded: ${error.message}`);

  writeRecordedSignature(context, signature);
  // The history changed, so a later restore skip is worth reporting again.
  removeStorage(restoreSkipReportedKey(context.scope));
};

const recordConsent = (
  context: SyncContext,
  consent: TrackingConsentDetails,
  signals?: Iterable<string> | null
): Promise<void> => {
  const submission = context.submission.then(() => submitConsent(context, consent, signals));
  // A failed submission rejects for its caller but must not wedge the queue.
  context.submission = submission.catch(() => undefined);

  return submission;
};

// A restore marks the whole snapshot confirmed, so it must cover exactly the purposes Airgap offers.
const isFullyRestorable = (
  context: SyncContext,
  preference: StoredPreference,
  purposes: TrackingConsent
): boolean => {
  const configurable = toPreferenceInput(context.airgap.getConsent().purposes);

  return (
    configurable.length > 0 &&
    configurable.length === preference.preferences.length &&
    configurable.every(({ purpose }) => purpose in purposes)
  );
};

// For consent confirmed on an earlier page there is no change event naming its signals, so active
// signals are sent only when everything is denied; anything allowed means the user overrode them.
const causalPrivacySignals = (airgap: AirgapAPI, consent: TrackingConsentDetails): string[] => {
  const optedOutOfEverything = toPreferenceInput(consent.purposes).every(
    ({ value }) => value === ConsentPreferenceValue.DENIED
  );
  if (!optedOutOfEverything) return [];

  try {
    return [...airgap.getPrivacySignals()];
  } catch {
    // Older bundles lack the accessor.
    return [];
  }
};

// The banner ignores a programmatic confirm. Cosmetic, so a failure here never fails the restore.
const hideConsentManager = async (): Promise<void> => {
  try {
    await window.transcend?.hideConsentManager();
  } catch {
    // The restored consent stands; the banner stays away from the next load.
  }
};

const restoreStoredPreference = async (
  context: SyncContext,
  preference: StoredPreference,
  interactions: InteractionEventSource
): Promise<void> => {
  const purposes = toAirgapPurposes(preference.preferences);
  if (!isFullyRestorable(context, preference, purposes)) {
    // A spike means stored snapshots no longer match the Transcend purposes; reported once per session.
    if (!readStorage(restoreSkipReportedKey(context.scope))) {
      writeStorage(restoreSkipReportedKey(context.scope), 'true');
      notify(new Error('Consent restore skipped: stored preference is not reproducible'));
    }

    return;
  }

  for (let attempt = 0; attempt < CONSENT_RESTORE_ATTEMPTS; attempt += 1) {
    const auth = await interactions.next();
    // Released: the user answered themselves or the sync stopped.
    if (!auth) return;

    // The user answered the banner while we waited; theirs is the newer choice.
    if (context.airgap.getConsent().confirmed) return;

    // Marked before setConsent so the change event it emits is not written back.
    writeRecordedSignature(context, preferenceSignature(preference.preferences));

    let restored = false;
    context.restoring = true;
    try {
      const options = { confirmed: true, prompted: true, timestamp: preference.consentGivenAt };
      // Returns a Promise only with options.waitForSync; the await satisfies the published type.
      restored = await context.airgap.setConsent(auth, purposes, options);
    } finally {
      context.restoring = false;
    }

    if (restored) {
      await hideConsentManager();
      return;
    }
  }
};

const handleConsentChange = async (
  context: SyncContext,
  event: ConsentChangeEventPayload
): Promise<void> => {
  // The restore's own change event is not a user choice.
  if (context.restoring) return;

  const consent = event.detail?.consent;
  // Inferred defaults are not a choice.
  if (!consent?.confirmed) return;

  context.interactions?.abort();

  await recordConsent(context, consent, event.detail.signals);
};

const runConsentSync = async (
  client: ApolloClient<object>,
  sessionToken: string,
  signal: AbortSignal
): Promise<void> => {
  const airgap = await whenAirgapReady();
  if (!airgap || signal.aborted) return;

  const context: SyncContext = {
    client,
    airgap,
    scope: sessionToken.slice(-CONSENT_SCOPE_LENGTH),
    restoring: false,
    interactions: null,
    submission: Promise.resolve(),
  };

  pruneForeignScopes(context.scope);

  airgap.addEventListener(
    CONSENT_CHANGE_EVENT,
    (event) => {
      handleConsentChange(context, event).catch(notify);
    },
    { signal }
  );

  const consent = airgap.getConsent();

  // A confirmed browser choice wins over the stored one.
  if (consent.confirmed) {
    await recordConsent(context, consent, causalPrivacySignals(airgap, consent));
    return;
  }

  // Armed before the read, so an interaction during it still counts.
  const interactions = createInteractionEventSource();
  context.interactions = interactions;
  signal.addEventListener('abort', interactions.stop, { once: true });
  try {
    const preference = await fetchStoredPreference(context);
    if (preference) await restoreStoredPreference(context, preference, interactions);
  } finally {
    context.interactions = null;
    interactions.stop();
  }
};

/**
 * Reconciles Transcend's browser consent with the signed-in user's stored preference: records a
 * confirmed browser choice, otherwise restores the stored one on the next user interaction.
 * Call the returned stop function on sign-out; an in-app logout keeps the page, so the listener
 * would outlive the session.
 */
export const startConsentSync = (
  client: ApolloClient<object>,
  sessionToken: string
): (() => void) => {
  const controller = new AbortController();
  runConsentSync(client, sessionToken, controller.signal).catch(notify);

  return () => controller.abort();
};
